import { useEffect, useState } from 'react'
import { formatScore, getApiBase, shortHash } from './utils.js'

export default function DetailDrawer({ detail, onClose }) {
  const [extra, setExtra] = useState(null)

  useEffect(() => {
    setExtra(null)
    if (!detail) return
    if (detail.kind === 'generator' && detail.dream?.generator_hash) {
      fetch(`${getApiBase()}/api/generators/${detail.dream.generator_hash}`)
        .then((r) => (r.ok ? r.json() : null))
        .then(setExtra)
        .catch(() => {})
    }
    if (detail.kind === 'evolution' && detail.population?.members?.[0]?.generator_hash) {
      const h = detail.population.members[0].generator_hash
      fetch(`${getApiBase()}/api/evolution/generator/${h}`)
        .then((r) => (r.ok ? r.json() : null))
        .then(setExtra)
        .catch(() => {})
    }
  }, [detail])

  if (!detail) return null

  return (
    <div className="mobi-drawer" role="dialog" aria-label="Möbi detail">
      <div className="mobi-drawer-head">
        <span className="dm-section">{titleFor(detail)}</span>
        <button type="button" className="mobi-link" onClick={onClose}>
          close
        </button>
      </div>
      <pre className="mobi-drawer-body">{renderBody(detail, extra)}</pre>
    </div>
  )
}

function titleFor(detail) {
  switch (detail.kind) {
    case 'dream':
      return 'DREAM DETAIL'
    case 'signals':
      return 'SIGNAL DETAIL'
    case 'map':
      return 'MAP DETAIL'
    case 'generator':
      return 'GENERATOR'
    case 'memory':
      return 'MEMORY'
    case 'evolution':
      return 'EVOLUTION'
    default:
      return 'DETAIL'
  }
}

function line(label, value) {
  return `${label.padEnd(14)}${value ?? '—'}\n`
}

function renderBody(detail, extra) {
  if (detail.kind === 'dream' || detail.kind === 'signals') {
    const d = detail.dream || {}
    return (
      line('Generation', d.generation) +
      line('Interest', formatScore(d.interest)) +
      line('Novelty', formatScore(d.novelty)) +
      line('Complexity', formatScore(d.complexity)) +
      line('Symmetry', formatScore(d.symmetry)) +
      line('Prediction', formatScore(d.predicted_interest)) +
      line('Surprise', formatScore(d.surprise)) +
      line('Curiosity', formatScore(d.curiosity)) +
      line('Uncertainty', formatScore(d.ensemble_uncertainty)) +
      line('Ensemble', d.ensemble_version) +
      line('Taste', d.taste_version) +
      line('Features', d.feature_schema_version) +
      line('Generator', d.generator_type) +
      line('Hash', d.generator_hash) +
      line('Family', d.generator_family) +
      line('Parent', d.parent_generator_hash) +
      line('Mutation', d.mutation_type) +
      line('Mut seed', d.mutation_seed) +
      line('Evo gen', d.evolution_generation)
    )
  }

  if (detail.kind === 'map') {
    const m = detail.map || {}
    const p = detail.point
    let out =
      line('Projection', m.projection_version) +
      line('Features', m.feature_schema_version) +
      line('Memories', m.valid_feature_count) +
      line('Invalid', m.invalid_feature_count) +
      line('PC1 var', m.explained_variance_ratio?.pc1) +
      line('PC2 var', m.explained_variance_ratio?.pc2)
    if (p) {
      out +=
        '\n' +
        line('Point gen', p.generation) +
        line('Generator', p.generator) +
        line('Interest', formatScore(p.interest)) +
        line('Value', formatScore(p.memory_value)) +
        line('x,y', `${Number(p.x).toFixed(3)}, ${Number(p.y).toFixed(3)}`)
    }
    return out
  }

  if (detail.kind === 'generator') {
    const d = detail.dream || {}
    let out =
      line('Type', d.generator_type) +
      line('Hash', d.generator_hash) +
      line('Family', d.generator_family) +
      line('Parent', d.parent_generator_hash) +
      line('Mutation', d.mutation_type) +
      line('Mut seed', d.mutation_seed) +
      line('Candidates', detail.generators?.count)
    if (extra?.pretty) {
      out += `\n${extra.pretty}\n`
    }
    if (extra?.status) {
      out += line('Status', extra.status) + line('Evals', extra.evaluation_count)
    }
    const cands = (detail.generators?.candidates || []).slice(0, 8)
    if (cands.length) {
      out += '\nRECENT\n'
      for (const c of cands) {
        out += `#${String(c.id).padStart(4, '0')}  ${c.family}  ${c.mutation_type || '—'}  ${shortHash(c.generator_hash)}\n`
      }
    }
    return out
  }

  if (detail.kind === 'memory') {
    const d = detail.dream || {}
    const m = d.memory || {}
    let out =
      line('Nearest', m.nearest_generation ?? m.nearest_memory_id) +
      line('Similarity', formatScore(m.nearest_similarity)) +
      line('Distance', formatScore(m.nearest_distance)) +
      line('LTM list', detail.memories?.length)
    const rows = (detail.memories || []).slice(0, 12)
    if (rows.length) {
      out += '\nLONG-TERM\n'
      for (const row of rows) {
        out += `#${String(row.generation).padStart(6, ' ')}  I ${formatScore(row.interest)}  V ${formatScore(row.memory_value)}\n`
      }
    }
    return out
  }

  if (detail.kind === 'evolution') {
    const e = detail.evolution || {}
    const stats = e.stats || detail.population?.stats || {}
    let out =
      line('Enabled', String(e.enabled)) +
      line('Version', e.evolution_version) +
      line('Fitness', e.fitness_version) +
      line('Generation', e.current_generation) +
      line('Cycles', e.cycle_count) +
      line('Population', e.population_size) +
      line('Elite', e.elite_count) +
      line('Diversity', stats.population_diversity != null ? Number(stats.population_diversity).toFixed(3) : '—') +
      line('Fit mean', stats.population_fitness_mean) +
      line('Fit max', stats.population_fitness_max) +
      line('Explore %', stats.exploration_fraction)
    const members = (detail.population?.members || []).slice(0, 8)
    if (members.length) {
      out += '\nTOP\n'
      for (const m of members) {
        out += `${m.rank}. #${shortHash(m.generator_hash)}  ${formatScore(m.fitness)}  ${m.slot_type}${m.is_elite ? ' *' : ''}\n`
        if (m.parent_hash) out += `   └ parent #${shortHash(m.parent_hash)}\n`
      }
    }
    if (extra?.pretty) {
      out += `\n${extra.pretty}\n`
    }
    return out
  }

  return '—'
}
